import React, { useCallback, useEffect, useState } from "react";
import { Send } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Modal from "../../Modal/Modal";
import Input from "../../../components/Input/Input";
import Notification from "../../../components/Notification/Notification";

/* ===================== SERVICES ===================== */
import { apiGet, apiPost } from "../../../apiService";
import { getErrorMessage } from "../../../utils/errorHelper";

/* ===================== STYLES ===================== */
import styles from "./InviteUserModal.module.scss";

const InviteUserModal = ({ isOpen, onClose, userType, onInviteSuccess }) => {
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState("");
  const [roles, setRoles] = useState([]);
  const [emailError, setEmailError] = useState("");
  const [roleError, setRoleError] = useState("");

  const [loadingRoles] = useState(false);
  const [loadingInvite, setLoadingInvite] = useState(false);
  const [notification, setNotification] = useState(null);

  // Fetch Roles
  const fetchRoles = useCallback(async () => {
    try {
      const typeParam = userType === "internal" ? "Internal" : "Client";
      const res = await apiGet("user", `/role?type=${typeParam}`);
      if (res.success) {
        setRoles(res.data || []);
      }
    } catch (error) {
      console.error(error);
      const msg = getErrorMessage(error);
      setNotification({
        type: "error",
        message: msg,
      });
    }
  }, [userType]);

  useEffect(() => {
    if (isOpen && userType === "internal") {
      fetchRoles();
    }
  }, [isOpen, userType, fetchRoles]);

  // Auto close after success
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
        if (notification.type === "success") {
          onClose();
        }
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [notification, onClose]);

  // Handle Invite User
  const handleInvite = async () => {
    let isValid = true;

    if (!email.trim()) {
      setEmailError("This field is required");
      isValid = false;
    } else if (!/^\S+@\S+\.\S+$/.test(email)) {
      setEmailError("Enter a valid email address");
      isValid = false;
    }

    if (userType === "internal" && !roleId) {
      setRoleError("This field is required");
      isValid = false;
    }

    if (!isValid) return;

    setLoadingInvite(true);
    try {
      const response = await apiPost("user", "/invite-user", {
        email,
        inviteType: userType === "internal" ? "INTERNAL" : "CLIENT",
        ...(userType === "internal" ? { roleId } : {}),
      });

      setNotification({
        type: "success",
        message: response?.message || "User invited successfully!",
      });

      onInviteSuccess();

      setEmail("");
      setRoleId("");
    } catch (error) {
      console.error(error);
      const msg = getErrorMessage(error);
      setNotification({
        type: "error",
        message: msg,
      });
    } finally {
      setLoadingInvite(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      title={
        userType === "client" ? "Invite Client User" : "Invite Internal User"
      }
      onClose={onClose}
    >
      {notification && (
        <div className={styles["admin-ium__notification-wrapper"]}>
          <Notification
            type={notification.type}
            message={notification.message}
          />
        </div>
      )}

      <div className={styles["admin-ium__form"]}>
        <Input
          label="Email ID"
          type="email"
          placeholder="Add comma separated emails to invite"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setEmailError("");
          }}
          error={emailError}
          required
        />

        {userType === "internal" && (
          <Input
            label="Role"
            type="select"
            placeholder={loadingRoles ? "Loading..." : "Select Role"}
            value={roleId}
            onChange={(e) => {
              setRoleId(e.target.value);
              setRoleError("");
            }}
            options={roles.map((r) => ({
              label: r.name,
              value: r.id,
            }))}
            error={roleError}
            required
            disabled={loadingRoles}
          />
        )}
      </div>

      <div className={styles["admin-ium__actions"]}>
        <button
          className={styles["admin-ium__submit-btn"]}
          onClick={handleInvite}
          disabled={loadingInvite}
        >
          <Send size={16} strokeWidth={2.5} />
          {loadingInvite ? "Inviting..." : "Invite"}
        </button>
      </div>
    </Modal>
  );
};

export default InviteUserModal;
